import React, { useEffect, useState } from 'react';
import { Button, Form, InputNumber, Select, Spin, Tag } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../redux/store';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/ToastComponent/store/toastMiddleware';
import {
  fetchBranchApprovalWorkflow,
  fetchBranches,
  fetchDefaultApprovalWorkflow,
  updateBranch,
  type ApprovalMode,
  type ApprovalWorkflow,
  type BranchResponse,
  type DocumentChargeRateKey,
  type SingleQueueRuleKey,
  type TieredRuleKey,
} from './addBranchApi';

const GENERIC_MESSAGE = 'Something went wrong. Please try again.';

const RATE_FIELDS: Array<{ key: DocumentChargeRateKey; label: string }> = [
  { key: 'loanDirect', label: 'Loan - direct' },
  { key: 'loanTakeover', label: 'Loan - takeover' },
  { key: 'loanRepledge', label: 'Loan - repledge' },
  { key: 'sale', label: 'Sale' },
];

// percent: the share of the expected document charge that is waived; amount: rupees
const TIERED_FIELDS: Array<{
  key: TieredRuleKey;
  label: string;
  unit: 'percent' | 'amount';
}> = [
  {
    key: 'loanDirectDocumentCharge',
    label: 'Loan document charge below expected (direct)',
    unit: 'percent',
  },
  {
    key: 'loanTakeoverDocumentCharge',
    label: 'Loan takeover charge below expected',
    unit: 'percent',
  },
  {
    key: 'loanRepledgeDocumentCharge',
    label: 'Loan document charge below expected (repledge)',
    unit: 'percent',
  },
  {
    key: 'loanAdditionalAmount',
    label: 'Loan additional amount',
    unit: 'amount',
  },
  {
    key: 'saleDocumentCharge',
    label: 'Sale document charge below expected',
    unit: 'percent',
  },
  {
    key: 'saleAdditionalAmount',
    label: 'Sale additional amount',
    unit: 'amount',
  },
];

const SINGLE_QUEUE_FIELDS: Array<{ key: SingleQueueRuleKey; label: string }> = [
  { key: 'pledgeBelowMinGoldRate', label: 'Pledge below minimum gold rate' },
  {
    key: 'loanPaymentInterestReduction',
    label: 'Loan payment with interest reduction',
  },
  { key: 'pledgePaymentExtra', label: 'Pledge payment with extra amount' },
];

const TIERED_OPTIONS: Array<{ value: ApprovalMode; label: string }> = [
  { value: 'none', label: 'No approval' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin (direct)' },
  { value: 'tiered', label: 'Manager up to limit, then admin' },
];

// Payments and pledges have a single approval queue
const SINGLE_QUEUE_OPTIONS: Array<{ value: ApprovalMode; label: string }> = [
  { value: 'none', label: 'No approval' },
  { value: 'admin', label: 'Admin (direct)' },
];

const required = { required: true, message: 'Required' };

export default function ApprovalWorkflowIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [form] = Form.useForm<ApprovalWorkflow>();
  const [branches, setBranches] = useState<BranchResponse[]>([]);
  const [branchId, setBranchId] = useState<string>();
  const [isBusy, setIsBusy] = useState(false);

  const handleError = (error: any) => {
    if (error?.response?.status === 401) {
      window.localStorage.clear();
      navigate('/sign-in');
      return;
    }
    const message = error?.response?.data?.message;
    dispatch(
      showErrorMessage(typeof message === 'string' ? message : GENERIC_MESSAGE)
    );
  };

  const run = async (task: () => Promise<void>) => {
    setIsBusy(true);
    try {
      await task();
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsBusy(false);
    }
  };

  useEffect(() => {
    void run(async () => {
      const list = await fetchBranches();
      setBranches(list);
      setBranchId(list[0]?._id);
    });
  }, []);

  useEffect(() => {
    if (branchId === undefined) return;
    void run(async () => {
      form.resetFields();
      form.setFieldsValue(await fetchBranchApprovalWorkflow(branchId));
    });
  }, [branchId]);

  const onFinish = async () => {
    if (branchId === undefined || isBusy) return;
    await run(async () => {
      // true: also the manager limits of rules that are not tiered right now, so they are kept
      const approvalWorkflow = form.getFieldsValue(true) as ApprovalWorkflow;
      const res = await updateBranch(branchId, { approvalWorkflow });
      dispatch(showSuccessMessage(res?.message ?? 'Approval workflow updated'));
    });
  };

  // Fills the form only: nothing changes until Save
  const resetToDefaults = () => {
    void run(async () => {
      form.setFieldsValue(await fetchDefaultApprovalWorkflow());
    });
  };

  return (
    <div className="settings__card settings__card--wide approval-workflow">
      <Spin spinning={isBusy} fullscreen />
      <div className="settings__head-row">
        <div>
          <p className="settings__title">Approvals</p>
          <p className="settings__subtitle">
            Decide which loans, sales, pledges and payments of a branch need a
            manager or an admin to approve them.
          </p>
        </div>
        <Select
          className="approval-workflow__branch"
          size="large"
          value={branchId}
          onChange={setBranchId}
          placeholder="Select branch"
          options={branches.map((branch) => ({
            value: branch._id,
            label: (
              <span>
                {branch.code} - {branch.name}
                {branch.status === 'deactive' && (
                  <Tag className="approval-workflow__tag">Deactive</Tag>
                )}
              </span>
            ),
          }))}
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        disabled={branchId === undefined}
      >
        <p className="approval-workflow__group">
          Expected document charge (% of principal)
        </p>
        <div className="approval-workflow__rates">
          {RATE_FIELDS.map((field) => (
            <Form.Item
              key={field.key}
              label={field.label}
              name={['documentChargeRates', field.key]}
              rules={[
                required,
                {
                  type: 'number',
                  min: 0,
                  max: 100,
                  message: 'Between 0 and 100',
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                step={0.05}
                addonAfter="%"
              />
            </Form.Item>
          ))}
        </div>

        <p className="approval-workflow__group">Loans and sales</p>
        <p className="settings__hint">
          For a document charge, the limit is how much of the expected charge
          the manager may waive. For an additional amount, it is the largest
          amount the manager may approve.
        </p>
        <div className="approval-workflow__table">
          <div className="approval-workflow__row approval-workflow__row--head">
            <span>Operation</span>
            <span>Who approves</span>
            <span>Manager limit</span>
          </div>
          {TIERED_FIELDS.map((field) => {
            const modePath = ['rules', field.key, 'mode'];
            return (
              <div className="approval-workflow__row" key={field.key}>
                <span className="approval-workflow__label">{field.label}</span>
                <Form.Item name={modePath} rules={[required]}>
                  <Select options={TIERED_OPTIONS} />
                </Form.Item>
                <Form.Item noStyle dependencies={[modePath]}>
                  {({ getFieldValue }) =>
                    getFieldValue(modePath) === 'tiered' ? (
                      <Form.Item
                        name={['rules', field.key, 'managerLimit']}
                        rules={[
                          required,
                          {
                            type: 'number',
                            min: 0,
                            max: field.unit === 'percent' ? 100 : undefined,
                            message:
                              field.unit === 'percent'
                                ? 'Between 0 and 100'
                                : '0 or more',
                          },
                        ]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          max={field.unit === 'percent' ? 100 : undefined}
                          addonBefore={
                            field.unit === 'amount' ? '₹' : undefined
                          }
                          addonAfter={
                            field.unit === 'percent' ? '%' : undefined
                          }
                        />
                      </Form.Item>
                    ) : (
                      <span className="approval-workflow__muted">-</span>
                    )
                  }
                </Form.Item>
              </div>
            );
          })}
        </div>

        <p className="approval-workflow__group">Pledges and payments</p>
        <div className="approval-workflow__table">
          <div className="approval-workflow__row approval-workflow__row--head">
            <span>Operation</span>
            <span>Who approves</span>
          </div>
          {SINGLE_QUEUE_FIELDS.map((field) => (
            <div className="approval-workflow__row" key={field.key}>
              <span className="approval-workflow__label">{field.label}</span>
              <Form.Item name={['rules', field.key, 'mode']} rules={[required]}>
                <Select options={SINGLE_QUEUE_OPTIONS} />
              </Form.Item>
            </div>
          ))}
        </div>

        <div className="settings__actions">
          <Button
            onClick={resetToDefaults}
            className="settings__button settings__button--ghost"
          >
            Reset to defaults
          </Button>
          <Button
            htmlType="submit"
            className="settings__button settings__button--primary"
          >
            Save
          </Button>
        </div>
      </Form>
    </div>
  );
}
